import { z } from 'zod'

/**
 * Application roles. A user carries a *set* of roles; authorisation decisions
 * are made by checking whether any required role is present in that set.
 * Typical combinations:
 *   - [user]                     — read-only operator
 *   - [admin]                    — backoffice admin (tablets, users, data sets)
 *   - [developer]                — schema registry only
 *   - [admin, developer]         — admin who also maintains schemas
 * There is no implicit hierarchy between roles; each capability must
 * explicitly list the roles it accepts.
 */
export const UserRoleSchema = z.enum(['user', 'admin', 'developer'])
export type UserRole = z.infer<typeof UserRoleSchema>

/**
 * At least one role is required — a user with no roles has no access at all
 * and would just take up a login slot. Duplicates are removed.
 */
export const UserRolesSchema = z
  .array(UserRoleSchema)
  .min(1, 'at least one role is required')
  .transform((roles) => [...new Set(roles)])

export const EmailSchema = z.string().email().max(254).toLowerCase()
export const PasswordSchema = z.string().min(12).max(256)

/**
 * Login identifier. Defaults to the email at user creation time, but the
 * admin can change it to anything from a single non-whitespace character up
 * to 254 chars. Whitespace-trimmed and lower-cased on the way in so that
 * the case-insensitive uniqueness index in the database is what callers
 * see.
 */
export const UsernameSchema = z
  .string()
  .trim()
  .min(1)
  .max(254)
  .refine((s) => !/\s/.test(s), { message: 'username must not contain whitespace' })
  .transform((s) => s.toLowerCase())

/**
 * UI languages the backoffice supports. `null` on a user means "no
 * preference stored"; the client then falls back to browser detection.
 */
export const LanguageCodeSchema = z.enum(['en', 'uk', 'de', 'cs'])
export type LanguageCode = z.infer<typeof LanguageCodeSchema>

export const UserSchema = z.object({
  id: z.string().uuid(),
  username: UsernameSchema,
  email: EmailSchema,
  roles: UserRolesSchema,
  language: LanguageCodeSchema.nullable(),
  /**
   * True while the user is still on a server-issued temporary password
   * (set on create or after an admin-driven reset). The web client gates
   * the rest of the app behind /change-password while this is true.
   */
  mustChangePassword: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
})

export const LoginRequestSchema = z.object({
  username: UsernameSchema,
  password: z.string().min(1).max(256),
})

export const RecoverRequestSchema = z.object({
  email: EmailSchema,
})

export const ResetPasswordRequestSchema = z.object({
  password: PasswordSchema,
})

/**
 * Username defaults to the email at the API layer when omitted. We keep it
 * optional in the request body so the create-user form doesn't have to ask
 * for it up front — the common case is "username = email address". The
 * password is generated server-side and returned in the response; the
 * caller never sets one directly.
 */
export const CreateUserRequestSchema = z.object({
  email: EmailSchema,
  username: UsernameSchema.optional(),
  roles: UserRolesSchema,
})

export const PatchUserRequestSchema = z.object({
  username: UsernameSchema.optional(),
  email: EmailSchema.optional(),
  roles: UserRolesSchema.optional(),
})

/**
 * Returned exactly once by POST /users and POST /users/:id/reset-password.
 * The plaintext temporary password is the only chance the admin gets to
 * see it — it is not stored anywhere recoverable. The recipient is forced
 * into the change-password flow on next login.
 */
export const TemporaryPasswordResultSchema = z.object({
  user: UserSchema,
  temporaryPassword: z.string(),
})

/**
 * `currentPassword` is optional — the server skips the verify step when
 * the user is still on a forced-change temporary password (the operator
 * just typed it in seconds ago to log in). For a normal voluntary change
 * the field is required and the API rejects an empty value.
 */
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().max(256).optional(),
  newPassword: PasswordSchema,
})

/**
 * Self-service profile patch body for the authenticated user. Additive
 * over time — for now only the language preference lives here.
 */
export const PatchMeRequestSchema = z.object({
  language: LanguageCodeSchema.nullable().optional(),
})

export function hasRole(
  user: { roles: UserRole[] } | { roles: readonly UserRole[] } | null | undefined,
  ...allowed: UserRole[]
): boolean {
  if (!user) return false
  return user.roles.some((r) => allowed.includes(r))
}

export type User = z.infer<typeof UserSchema>
export type LoginRequest = z.infer<typeof LoginRequestSchema>
export type RecoverRequest = z.infer<typeof RecoverRequestSchema>
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>
export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>
export type PatchUserRequest = z.infer<typeof PatchUserRequestSchema>
export type PatchMeRequest = z.infer<typeof PatchMeRequestSchema>
export type TemporaryPasswordResult = z.infer<typeof TemporaryPasswordResultSchema>
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>
